import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type {
  DocumentServicesObject,
  WidgetData,
} from '@wix/document-services-types';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../../types/tpaMessage';
import { SelectedComponentData } from '../utils/handlerUtils';

interface TPAWidget extends WidgetData {
  appPage: { id: string };
  tpaWidgetId: string;
}

interface AppEngagedMessageData {
  premiumIntent?: string;
}

export function createInstallationHandlers(
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  getSelectedComponentAppData: () => Promise<SelectedComponentData>,
) {
  const isComponentInstalled: TPAHandler = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const tpaComponentId = msg.data?.componentId;
    const { appData } = await getSelectedComponentAppData();

    if (appData) {
      const appDataWidgets = appData.widgets;
      if (appDataWidgets) {
        const widgets = Object.entries(appData.widgets).map(
          ([_appDefId, widget]) => widget as TPAWidget,
        );
        const widgetData = widgets.find((widget) => {
          if (widget.appPage) {
            return widget.appPage.id === tpaComponentId;
          }
          return widget.tpaWidgetId === tpaComponentId;
        });
        if (widgetData?.widgetId) {
          const installed = documentServicesAPI.tpa.comp.isInstalled(
            widgetData.widgetId,
          );
          callback(installed);
          return;
        }
      }
    }
    callback(false);
  };

  const isApplicationInstalled: TPAHandler = (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const appDefinitionId = msg.data?.appDefinitionId;
    if (!appDefinitionId) {
      throw new Error(
        '"appDefinitionId" should be present in the message data for isApplicationInstalled handler',
      );
    }
    callback(documentServicesAPI.tpa.app.isInstalled(appDefinitionId));
  };

  const appEngaged: TPAHandler<AppEngagedMessageData> = async (msg) => {
    const { appData } = await getSelectedComponentAppData();
    if (!appData) {
      return;
    }
    const params = {
      app_id: appData.appDefinitionId,
      instance_id: appData.instanceId,
      premium_intent: msg.data?.premiumIntent,
    };
    platformApiMethods.editor.onAppEngaged?.(params);
  };

  return {
    isComponentInstalled,
    isApplicationInstalled,
    editorIsApplicationInstalled: isApplicationInstalled,
    appEngaged,
  };
}
